import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import Link from 'next/link'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = getArticleDateTime();
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        countryName,
        currencyCd,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const requestType = 'Aircon Services'
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Aircon Condenser Fan Motor Replacement Guide & Repair Cost</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Aircon condenser fan motor replacement can cost between $250 to $750 depending on the brand, model specifications and the technician fees. Learn about how to replace the motor yourself."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-fan-motor" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Aircon Condenser Fan Motor Replacement Guide & Repair Cost`}/>
        <meta property="og:description" content="Aircon condenser fan motor replacement can cost between $250 to $750 depending on the brand, model specifications and the technician fees. Learn about how to replace the motor yourself." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-18T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-condenser-cleaning.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-condenser-cleaning.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-fan-motor"/>
        <meta name="twitter:title" content={`Aircon Condenser Fan Motor Replacement Guide & Repair Cost`}/> 
        <meta name="twitter:description" content="Aircon condenser fan motor replacement can cost between $250 to $750 depending on the brand, model specifications and the technician fees. Learn about how to replace the motor yourself."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/condenser-fan-motor"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-fan-motor"},"headline":"Faulty Aircon Condenser Fan Motor Replacement Guide & Repair Cost","description":"Aircon condenser fan motor replacement can cost between $250 to $750 depending on the brand, model specifications and the technician fees. Learn about how to replace the motor yourself.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-condenser-cleaning.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-18T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/condenser-fan-motor","name":"Condenser Fan Motor","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/condenser-fan-motor"}}]}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-compressor-motor" className='relative'>
                    <AirconRepair 
                        setRequestForm={setRequestForm} 
                        setIsQuickBook={setIsQuickBook} 
                        title={"Aircon Compressor Fan Motor Replacement Guide & Repair Cost"} 
                        problem={"Replace Condenser Fan Motor for [brand name]"}
                        heroImage="aircon/noisy-aircon-compressor-minified.png"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-10 px-2 leading-8'>
                <div className='py-2 space-y-5'>
                    <div className='space-y-2'>
                        <h2 className='font-bold text-xl md:text-2xl uppercase'>What is an aircon compressor fan motor?</h2>
                        <p className=''>
                            The air conditioner condenser fan motor is a vital component of the aircon system. The condenser helps to release heat absorbed from indoor to the outside 
                            environment. It plays an important role in the cooling process of air conditioners.
                        </p>
                        <p>
                            The condenser fan motor drives the fan blades that help move air across the condenser coils. These coils contain the refrigerant that has absorbed heat from the 
                            indoor air.  When the fan motor rotates the condenser fan, the heat is released to outdoor air, which facilitates the cooling of the refrigerant.
                        </p>
                        <p>
                            By maintaining proper airflow across the condenser coils, the condenser fan motor ensures efficient heat exchange and helps the air conditioning system 
                            operate effectively. If the condenser fan motor malfunctions or fails, it can lead to reduced cooling efficiency, increased energy consumption, and 
                            potential damage to the compressor and other components of the air conditioning system.
                        </p>
                    </div>                     
                    <div className='relative h-96'>
                        <Image className='rounded-lg'
                            src={s3Static + "blog/living/air-conditioner-condenser-cleaning.jpg"}
                            fill
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon condenser fan motor singapore'
                        />          
                    </div>
                    <div className='space-y-4'>
                        <h2 className='font-bold text-2xl'>How to replace aircon condenser unit fan motor (DIY)?</h2>
                        <p>
                            Replacing an air conditioning  condenser fan motor is a task that requires technical knowledge and experience with HVAC systems. If you&apos;re not familiar with 
                            working on A/C equipment, it&apos;s recommended to hire a professional technician to perform the replacement. Here&apos;s a general outline of the steps involved in
                             replacing an aircon condenser fan motor:
                        </p>
                        <h3 className='font-bold'>Replacement Steps:</h3>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>1. Safety Precautions</h3>
                            <p>
                                Before starting any work, ensure the power to the air conditioning system is turned off at the circuit breaker or disconnect switch to prevent electrical 
                                accidents.
                            </p> 
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>2. Gather Equipment and Parts</h3>
                            <p>
                                You will need a replacement condenser fan motor that matches the specifications of the original motor. Make sure you have the necessary tools, including wrenches, 
                                screwdrivers, pliers, and potentially a multimeter.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>3. Access the Condenser Unit</h3>
                            <p>
                                Open the access panel on the condenser unit. This panel is typically located on the side or top of the unit and may require removing screws or fasteners.
                            </p>
                        </div>     
                        <div className='space-y-1'>
                            <h3 className='font-bold'>4. Disconnect Wires</h3>
                            <p>
                                Carefully disconnect the electrical wires from the existing fan motor. Take note of their connections or take pictures to ensure correct reconnection later.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>5. Remove the Fan Blade</h3>
                            <p>
                                Remove the fan blade from the motor shaft. This may involve removing a retaining nut or bolt. Be cautious not to damage the fan blade.
                            </p>
                        </div>   
                        <div className='space-y-1'>
                            <h3 className='font-bold'>6. Detach the Motor</h3>
                            <p>
                                The motor is usually held in place by mounting brackets or screws. Remove these brackets or screws to free the motor from the unit.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>7. Install the New Motor:</h3>
                            <p>
                                Position the new motor in the same location as the old one. Reattach any mounting brackets or screws that hold the motor securely in place.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>8. Reattach Fan Blade</h3>
                            <p>
                                Slide the fan blade onto the motor shaft and secure it with the retaining nut or bolt.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>9. Connect Electrical Wires</h3>
                            <p>
                                Attach the electrical wires to the new motor in the same configuration as the old motor. Use wire connectors or terminal blocks as necessary.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>10. Test Operation</h3>
                            <p>
                                Double-check all connections, ensuring they are secure. Turn the power back on at the circuit breaker or disconnect switch and test the operation of the new motor. Listen for any unusual noises or vibrations.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>11. Check Cooling Performance</h3>
                            <p>
                                Monitor the air conditioning system&apos;s cooling performance over a period to ensure that the new fan motor is functioning properly and maintaining proper heat exchange.
                            </p>
                        </div>
                        <div className='space-y-1'>
                            <h3 className='font-bold'>12. Replace Access Panel</h3>
                            <p>
                                Once you are satisfied with the installation and performance, securely replace the access panel on the condenser unit.
                            </p>
                        </div>
                        <p>
                            Remember that air conditioning systems involve electrical components and refrigerants, so safety and proper installation are paramount. If you&apos;re not 
                            confident in your abilities, it&apos;s best to seek assistance from a qualified HVAC technician. An improper installation could lead to equipment damage, 
                            inefficient cooling, or safety hazards.
                        </p>
                    </div>                   
                    <div className='relative h-96'>
                        <Image className='rounded-lg'
                            src={s3Static + "blog/living/aircon-condenser.jpg"}
                            fill
                            style={{objectFit: 'cover', objectPosition: 'left'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon condenser installed outside'
                        />          
                    </div>
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>Indications of a Faulty Condenser Fan Motor</h2>
                        <div className='space-y-3'>
                            <p className=''>
                            A faulty condenser fan motor in an air conditioning system can lead to various issues that impact the system&apos;s performance and efficiency. Here are some indications that your condenser fan motor might 
                            be malfunctioning:
                            </p>
                            <div className='space-y-1'>
                                <h3 className='font-bold'>1. Lack of Cooling</h3>
                                <p>
                                One of the most common signs of a faulty condenser fan motor is a noticeable decrease in cooling performance. If the fan motor fails, the condenser coils won&apos;t receive proper airflow, leading to 
                                inefficient heat exchange and reduced cooling capacity.
                                </p>
                            </div>
                            <div className='space-y-1'>
                                <h3 className='font-bold'>2. Warm Air Blowing</h3>
                                <p>
                                If warm air is blowing from your air conditioning vents instead of cool air, it could be due to a malfunctioning condenser fan motor. Without proper cooling of the refrigerant, 
                                the system won&apos;t be able to effectively remove heat from indoor air.
                                </p>
                            </div>
                            <div className='space-y-1'>
                                <h3 className='font-bold'>3. Loud or Unusual Noises</h3>
                                <p>
                                A malfunctioning fan motor may produce grinding, squealing, or rattling noises as it struggles to turn the fan blades. These noises can be indicative of 
                                worn bearings, loose components, or other mechanical issues.
                                </p>
                            </div>     
                            <div className='space-y-1'>
                                <h3 className='font-bold'>4. Overheating of the System</h3>
                                <p>
                                A malfunctioning fan motor can lead to the condenser coils overheating, causing the system to shut down or go into a protective mode to prevent further damage.
                                </p>
                            </div>
                            <div className='space-y-1'>
                                <h3 className='font-bold'>5. Fan Not Spinning</h3>
                                <p>
                                If the fan blades are not spinning when the air conditioning unit is running, it&apos;s a clear indication that the condenser fan motor is not functioning properly.
                                </p>
                            </div>              
                        </div>
                    </div>
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>Condenser Fan Motor Replacement Cost</h2>
                        <div className='space-y-5'>
                            <p>
                                Condenser fan motor replacement can cost anywhere between $250 to $750, depending on the brand, model specifications and labor or technician fees. To get
                                a ballpark estimate open a repair request with us and mention the brand and model specifications.
                            </p>
                        </div>
                    </div>             
                </div> 
            </div>
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> 
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}